import React from 'react'
import PropTypes from 'prop-types'
import { compose } from 'recompose'
import {
  Field,
  SubmissionError,
  propTypes as formPropTypes,
  formValues,
} from 'redux-form'
import { isEmpty } from 'lodash'
import { lpForm } from 'lp-form'
import { MobilePushNotificationLabel, SmsNotificationLabel } from 'components'
import {
  Button,
  Checkbox,
  Input,
  MaskedInput,
  Select,
  SubmitButton,
} from 'lp-components'
import {
  CONTACT_MEMBER_RELATIONSHIPS,
  CONTACT_PARTNER_RELATIONSHIPS,
  isEmailBasedLoginStrategy,
  isPasswordlessLoginStrategy,
  isSocialLoginStrategy,
} from 'types'
import { PHONE_REGION_CODE } from 'config'
import { parsePhoneNumber, useConfirm } from 'utils'
import { Tooltip } from 'react-tooltip'

// One modal covers an email change, a mobile-number change, or both at once.
function buildLoginChangeModal({ emailChanging, mobilePhoneChanging }) {
  const messages = []
  if (emailChanging) {
    messages.push(
      'Updating your email also updates how you sign in. You will be signed out and need to sign in again with your new email.',
    )
  }
  if (mobilePhoneChanging) {
    messages.push(
      'Your sign-in code will be sent to your new mobile number. Make sure you can receive text messages there first.',
    )
  }
  return {
    heading: 'Update Your Sign-In Details',
    prompt: messages.join('\n\n'),
    confirmLabel: emailChanging ? 'Update & Sign Out' : 'Update Login Number',
    cancelLabel: 'Cancel',
    preventClose: true,
  }
}

const propTypes = {
  country: PropTypes.string,
  email: PropTypes.string,
  id: PropTypes.string.isRequired,
  isMemberContact: PropTypes.bool.isRequired,
  isPrimaryContact: PropTypes.bool.isRequired,
  loginStrategy: PropTypes.string.isRequired,
  mobilePhone: PropTypes.string,
  toggleContactForm: PropTypes.func,
  ...formPropTypes,
}

function ContactForm({
  change,
  country = '',
  email = '',
  handleSubmit,
  id,
  initialValues,
  isMemberContact,
  isPrimaryContact,
  loginStrategy,
  mobilePhone = '',
  toggleContactForm,
}) {
  const { isConfirmed: confirmModal } = useConfirm()

  const isEmailLogin = isEmailBasedLoginStrategy(loginStrategy)
  const isPasswordless = isPasswordlessLoginStrategy(loginStrategy)
  const isSocialLogin = isSocialLoginStrategy(loginStrategy)
  const isEmailDisabled = isSocialLogin && isPrimaryContact

  const emailChangesLogin =
    isPrimaryContact && isEmailLogin && initialValues.email !== email

  // Any passwordless member signs in with their mobile number, even if they
  // used a different method this session. Clearing the number isn't a login
  // change (they can still sign in by email), so don't prompt for it.
  const mobilePhoneChangesLogin =
    isPrimaryContact &&
    isPasswordless &&
    !!mobilePhone &&
    initialValues.mobilePhone !== mobilePhone

  // Cancelling reverts whichever credential fields changed.
  const confirmLoginChange = async () => {
    const confirmed = await confirmModal(
      buildLoginChangeModal({
        emailChanging: emailChangesLogin,
        mobilePhoneChanging: mobilePhoneChangesLogin,
      }),
    )
    if (confirmed) {
      handleSubmit()
    } else {
      if (emailChangesLogin) change('email', initialValues.email)
      if (mobilePhoneChangesLogin) {
        change('mobilePhone', initialValues.mobilePhone)
      }
    }
  }

  const handleContactFormSubmit = (e) => {
    e.preventDefault()
    if (emailChangesLogin || mobilePhoneChangesLogin) {
      confirmLoginChange()
    } else {
      handleSubmit()
    }
  }

  return (
    <form onSubmit={handleContactFormSubmit} noValidate>
      <div className="group">
        <div className="row">
          <div className="col-6">
            <Field
              component={Input}
              label="First Name"
              name="firstName"
              required
              requiredIndicator="*"
            />
          </div>
          <div className="col-6">
            <Field
              component={Input}
              label="Last Name"
              name="lastName"
              required
              requiredIndicator="*"
            />
          </div>
        </div>
        {!initialValues && (
          <div className="row">
            <div className="col-6">
              <Field
                component={Select}
                label={isMemberContact ? 'Relationship' : 'Role'}
                name="relationship"
                options={
                  isMemberContact
                    ? CONTACT_MEMBER_RELATIONSHIPS
                    : CONTACT_PARTNER_RELATIONSHIPS
                }
                placeholder={'Select'}
                required
                requiredIndicator="*"
              />
            </div>
          </div>
        )}
        {isEmailDisabled && <Tooltip id={id} />}
        <Field
          data-tooltip-id={id}
          data-tooltip-content="Manage directly with your social identity provider."
          data-tooltip-place="top-start"
          component={Input}
          disabled={isEmailDisabled}
          label="Email"
          name="email"
          required={isPrimaryContact}
          requiredIndicator="*"
        />
        <div className="row">
          <div className="col-6">
            <Field
              component={MaskedInput}
              label="Home Phone"
              maskOptions={{
                numericOnly: true,
                blocks: [0, 3, 0, 3, 4],
                delimiters: ['(', ')', ' ', '-'],
                phoneRegionCode: PHONE_REGION_CODE[country],
              }}
              name="phone"
              parse={parsePhoneNumber}
              placeholder="(800) 555-1212"
            />
          </div>
          <div className="col-6">
            <Field
              component={MaskedInput}
              label="Mobile Phone"
              maskOptions={{
                numericOnly: true,
                blocks: [0, 3, 0, 3, 4],
                delimiters: ['(', ')', ' ', '-'],
                phoneRegionCode: PHONE_REGION_CODE[country],
              }}
              name="mobilePhone"
              parse={parsePhoneNumber}
              placeholder="(800) 555-1212"
            />
          </div>
        </div>
      </div>
      {isMemberContact && (
        <>
          <div className="group">
            <h3>Email Preferences</h3>
            <Field name="seaTowNews" component={Checkbox} label="SeaTow News" />
            <Field
              name="benefitsAndOffers"
              component={Checkbox}
              label="Benefits And Offers"
            />
            <Field
              name="localCaptainsCorner"
              component={Checkbox}
              label="Local Captain's Corner"
            />
          </div>
          <div className="group">
            <h3>SMS Opt-In</h3>
            <Field
              name="smsOptIn"
              component={Checkbox}
              labelComponent={(props) => <SmsNotificationLabel {...props} />}
            />
          </div>
          <div className="group">
            <h3>Mobile Push Notification Opt-In</h3>
            <Field
              name="mobilePushOptIn"
              component={Checkbox}
              labelComponent={(props) => (
                <MobilePushNotificationLabel {...props} />
              )}
            />
          </div>
        </>
      )}

      <div className="button-group">
        {initialValues && (
          <Button
            onClick={toggleContactForm}
            className="button-primary-outline"
          >
            Cancel
          </Button>
        )}
        <SubmitButton>
          {initialValues ? 'Update Contact' : 'Submit'}
        </SubmitButton>
      </div>
    </form>
  )
}

const beforeSubmit = (contactFormData, { initialValues, isPrimaryContact }) => {
  if (!initialValues) {
    const relationship = contactFormData['relationship']
    if (!relationship) {
      throw new SubmissionError({
        relationship: 'Relationship must be specified',
      })
    }
  }

  if (isPrimaryContact) {
    const { email, phone, mobilePhone, smsOptIn } = contactFormData

    if (isEmpty(email)) {
      throw new SubmissionError({
        email: 'Email must be specified for the primary contact',
      })
    }
    if (isEmpty(phone) && isEmpty(mobilePhone)) {
      throw new SubmissionError({
        phone:
          'either Home Phone or Mobile Phone must be specfied for the primary contact',
      })
    }

    if (smsOptIn && !mobilePhone) {
      throw new SubmissionError({
        smsOptIn:
          'SMS Opt-In cannot be checked without providing a Mobile Phone number',
      })
    }
  }

  return contactFormData
}

ContactForm.propTypes = propTypes

export default compose(
  lpForm({
    beforeSubmit,
    constraints: {
      firstName: { presence: true },
      lastName: { presence: true },
    },
  }),
  formValues('email', 'mobilePhone'),
)(ContactForm)
